import React, { useMemo } from 'react';
import { CheckIcon, ClockIcon } from 'lucide-react';
import { CONFIG } from '../data/config';
import { DIAS } from '../utils/format';
import { campo, etiqueta, opcional } from './estilos';
import { Paso } from './Paso';
import type { DatosContacto, ProximaEntrega, Zona } from '../types/pedido';
export const FUERA_DE_ZONA = '__otro__';
interface Props {
  valorBarrio: string;
  zona: Zona | null;
  fueraDeZona: boolean;
  entrega: ProximaEntrega | null;
  datos: DatosContacto;
  onBarrio: (valor: string) => void;
  onDato: (campo: keyof DatosContacto, valor: string) => void;
}
export function PasoZona({
  valorBarrio,
  zona,
  fueraDeZona,
  entrega,
  datos,
  onBarrio,
  onDato
}: Props) {
  const grupos = useMemo(() => {
    const porDia: Record<number, string[]> = {};
    CONFIG.zonas.forEach(z => z.entregas.forEach(en => {
      porDia[en.dia] = porDia[en.dia] ?? [];
      porDia[en.dia].push(z.barrio);
    }));
    return Object.keys(porDia).map(Number).sort((a, b) => a - b).map(dia => ({
      dia,
      barrios: porDia[dia].sort((a, b) => a.localeCompare(b, 'es'))
    }));
  }, []);
  const diasDelBarrio = zona ? zona.entregas.map(en => DIAS[en.dia]).join(' y ') : '';
  const mostrarDireccion = Boolean(valorBarrio);
  return <Paso num="01" titulo="¿A dónde lo llevamos?" data-id="element-99" data-mp="31zprxvp6ef50">
      <label className="mb-3 block" data-id="element-100" data-mp="2x04p3a6vxmn0">
        <span className={etiqueta} data-id="element-101" data-mp="2x161bhzn0f6b">Tu barrio</span>
        <select value={valorBarrio} onChange={e => onBarrio(e.target.value)} className={`${campo} select-field pr-9`} required data-id="element-102" data-mp="29aaekza1ga5x">
          <option value="" data-id="element-103" data-mp="2iaf9wb1yji3q">Elegí tu barrio…</option>
          {grupos.map(g => <optgroup key={g.dia} label={`Pasamos los ${DIAS[g.dia]}`} data-id="element-104" data-mp="2e8bzlpr0yjk1">
              {g.barrios.map(barrio => <option key={`${g.dia}-${barrio}`} value={barrio} data-id="element-105" data-mp="3tojba2avk8it">
                  {barrio}
                </option>)}
            </optgroup>)}
          <option value={FUERA_DE_ZONA} data-id="element-106" data-mp="3pptv61ygccvh">Mi barrio no está en la lista</option>
        </select>
      </label>

      {zona && entrega && <Aviso tono="ok" data-id="element-107" data-mp="2rav9tutm8129">
          En <b className="font-bold" data-id="element-108" data-mp="2ra3gkqnhqff5">{zona.barrio}</b> pasamos los{' '}
          <b className="font-bold" data-id="element-109" data-mp="2wahkbwi2737d">{diasDelBarrio}</b>. Tu pedido entra para{' '}
          {entrega.esHoy ? 'hoy' : entrega.esManana ? 'mañana' : <b className="font-bold" data-id="element-110" data-mp="23n50hu3a99hr">
              el {DIAS[entrega.dia]} {entrega.txt}
            </b>}
          .
        </Aviso>}

      {fueraDeZona && <Aviso tono="espera" data-id="element-111" data-mp="2cn0d83chlcjw">
          Todavía no tenemos recorrido fijo por ahí. Dejanos igual tu dirección: si juntamos varios pedidos en la zona,
          abrimos día y te avisamos.
        </Aviso>}

      {mostrarDireccion && <div className="mt-3" data-id="element-112" data-mp="1kv2qv2p26th0">
          <label className="mb-3 block" data-id="element-113" data-mp="1ktrmb0nshhou">
            <span className={etiqueta} data-id="element-114" data-mp="1owebett2soic">Calle y número</span>
            <input type="text" value={datos.calle} onChange={e => onDato('calle', e.target.value)} placeholder="Av. Maipú 2350" autoComplete="street-address" className={campo} required data-id="element-115" data-mp="1outgv46rh5kh" />
          </label>
          <div className="grid grid-cols-2 gap-2.5" data-id="element-116" data-mp="13e5k0brkb963">
            <label className="block" data-id="element-117" data-mp="13dm2ux9qkot4">
              <span className={etiqueta} data-id="element-118" data-mp="yeix1alxmek3">
                Piso / depto <span className={opcional} data-id="element-119" data-mp="ygdm9148kstz">(opcional)</span>
              </span>
              <input type="text" value={datos.piso} onChange={e => onDato('piso', e.target.value)} placeholder="4º B" className={campo} data-id="element-120" data-mp="tn151c1vzob9" />
            </label>
            <label className="block" data-id="element-121" data-mp="fob9i0uptwp0">
              <span className={etiqueta} data-id="element-122" data-mp="fn9l6uoovdpc">
                Entre calles <span className={opcional} data-id="element-123" data-mp="ap842fatpogi">(opcional)</span>
              </span>
              <input type="text" value={datos.entre} onChange={e => onDato('entre', e.target.value)} placeholder="Rivadavia y Alsina" className={campo} data-id="element-124" data-mp="arb4lt9lqzzh" />
            </label>
          </div>
        </div>}
    </Paso>;
}
function Aviso({
  tono,
  children
}: {
  tono: 'ok' | 'espera';
  children: React.ReactNode;
}) {
  const ok = tono === 'ok';
  const Icono = ok ? CheckIcon : ClockIcon;
  return <div className={`flex items-start gap-2.5 rounded-field px-3 py-2.5 text-sm leading-snug ${ok ? 'bg-good-soft text-good' : 'bg-warn-soft text-warn'}`} data-id="element-125" data-mp="1rzommcyc8dtu">
      <Icono className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.4} aria-hidden="true" data-id="element-126" data-mp="1i4d55mvjosie" />
      <div data-id="element-127" data-mp="1n2y99xdx3wc4">{children}</div>
    </div>;
}